// -- Permanent types; permissions and checks will continue to live in code

export enum Permission {
  switchOrganization = "switchOrganization",
  debugApps = "debugApps",
  useMobile = "useMobile",
  useWebApp = "useWebApp",
  manageProviderShifts = "manageProviderShifts",
  scheduleVisits = "scheduleVisits",
  manageAnyVisit = "manageAnyVisit",
  impersonateAnyProvider = "impersonateAnyProvider",
  viewProviderLocations = "viewProviderLocations",
  manageOwnAvailability = "manageOwnAvailability",
  importPatients = "importPatients",
  managePatients = "managePatients",
  manageUsers = "manageUsers",
  viewKPIs = "viewKPIs",
  manageOrgConfig = "manageOrgConfig",
  createOverrideVisits = "createOverrideVisits",
  createDragEvents = "createDragEvents",
  useVisitWorklist = "useVisitWorklist",
  toggleManageOwnAvailability = "toggleManageOwnAvailability",
  importUsers = "importUsers",
  manageApiKeys = "manageApiKeys",
  manageTeams = "manageTeams",
  optimizeSchedule = "optimizeSchedule",
  manageCalendarBlocks = "manageCalendarBlocks",
  manageVisitStatus = "manageVisitStatus",
  copyDemoData = "copyDemoData",
  takeCandidacy = "takeCandidacy",
  assignCandidacy = "assignCandidacy",
  manageSosAlerts = "manageSosAlerts",
  manageSupplies = "manageSupplies",
  useSupplies = "useSupplies",

  // This is a special permission - all users always have it implicitly.
  // Use sparingly; *any* logged in user can run queries marked
  // with this permission, even if all their roles have been removed
  // (eg, account disabled).
  isAuthenticated = "isAuthenticated",
}

export enum PermissionCheck {
  // Checks $userId variable, verifying that the user is asking about themselves.
  // impersonateAnyProvider permission always passes check
  // No additional DB queries.
  userIsSelf = "userIsSelf",

  // Checks $providerId variable, verifying that the user is that provider.
  // impersonateAnyProvider permission always passes check
  // No additional DB queries.
  userIsProvider = "userIsProvider",

  // Checks $visitId variable, verifying that the user is a provider on a VisitResource for this visit.
  // impersonateAnyProvider permission always passes check
  // Runs one DB query.
  userIsProviderOnVisit = "userIsProviderOnVisit",

  userIsProviderOnSosAlert = "userIsProviderOnSosAlert",

  // Checks $serviceLineId variable, verifying that the user is associated with that service line
  // No additional DB queries.
  userInServiceLine = "userInServiceLine",

  // Checks $visitId variable, verifying that the user is associated with that visit's service line
  // impersonateAnyProvider permission always passes check
  // Runs one DB query.
  userInVisitServiceLine = "userInVisitServiceLine",

  // Checks $episodeId variable, verifying that the user is associated with that episode's service line
  // impersonateAnyProvider permission always passes check
  // Runs one DB query.
  userInEpisodeServiceLine = "userInEpisodeServiceLine",
}

export const permissionDescriptions: Record<Permission, string> = {
  switchOrganization: "switch organizations",
  debugApps: "view debugging information",
  useMobile: "view your schedule",
  useWebApp: "access the web interface",
  manageProviderShifts: "manage provider shifts",
  scheduleVisits: "schedule patient visits",
  manageAnyVisit: "manage patient visits",
  impersonateAnyProvider: "impersonate any provider",
  viewProviderLocations: "view live provider locations",
  manageOwnAvailability: "manage your availability",
  importPatients: "import patient records",
  managePatients: "add or edit patient records",
  manageUsers: "add/edit/remove users and providers",
  isAuthenticated: "sign in",
  viewKPIs: "view kpi dashboard",
  manageOrgConfig: "manage service lines and visit types",
  createOverrideVisits: "create visits from the command center, bypassing the normal schedule workflow",
  createDragEvents: "use drag and drop scheduling in the command center",
  useVisitWorklist: "use the visit worklist feature",
  toggleManageOwnAvailability: "configure providers to manage their own availability",
  importUsers: "import user records",
  manageApiKeys: "manage api keys",
  manageTeams: "manage provider teams",
  optimizeSchedule: "optimize drive time for a whole day and re-assign in-home providers",
  manageCalendarBlocks: "manage calendar blocks",
  manageVisitStatus: "advance or modify visit statuses on behalf of assigned providers",
  copyDemoData: "copy data between days for demo purposes",
  takeCandidacy: "take (self-assign) or release a discharge candidacy",
  assignCandidacy: "assign a discharge candidacy to another user",
  manageSosAlerts: "acknowledge and resolve SOS alerts",
  manageSupplies: "create and manage inventory supplies and barcodes",
  useSupplies: "view supply inventory and record stock changes",
}

// Use a-z then A-Z then aa-ZZ, etc.
// DANGER: THESE ARE STORED IN CLIENT SIDE COOKIES
// DO NOT REASSIGN THEM WITHOUT FIRST ALLOWING SUFFICIENT
// TIME FOR ALL EXISTING COOKIES TO EXPIRE. OTHERWISE YOU
// RISK ACCIDENTALLY GRANTING UNINTENDED PERMISSIONS TO USERS
const minifiedPermission: Record<Permission, string> = {
  switchOrganization: "a",
  debugApps: "b",
  useMobile: "c",
  useWebApp: "d",
  manageProviderShifts: "e",
  scheduleVisits: "f",
  manageAnyVisit: "g",
  impersonateAnyProvider: "h",
  viewProviderLocations: "i",
  manageOwnAvailability: "j",
  importPatients: "k",
  managePatients: "l",
  manageUsers: "m",
  isAuthenticated: "n",
  viewKPIs: "o",
  manageOrgConfig: "p",
  createOverrideVisits: "q",
  createDragEvents: "r",
  useVisitWorklist: "s",
  toggleManageOwnAvailability: "t",
  importUsers: "u",
  manageApiKeys: "v",
  manageTeams: "w",
  optimizeSchedule: "x",
  manageCalendarBlocks: "y",
  manageVisitStatus: "z",
  copyDemoData: "A",
  takeCandidacy: "B",
  assignCandidacy: "C",
  manageSosAlerts: "D",
  manageSupplies: "E",
  useSupplies: "F",
}

const permissionFromMini: Record<string, Permission> = (Object.keys(minifiedPermission) as Permission[]).reduce((acc, p) => {acc[minifiedPermission[p]]=p as Permission; return acc;}, {} as Record<string, Permission>);

const MINIFICATION_DELIMITER = ",";

export const minifyPermissions = (permissions: Permission[]): string => {
  return permissions.map(p => minifiedPermission[p]).join(MINIFICATION_DELIMITER);
}

export const inflateMinifiedPermissions = (minifiedPermissions: string): Permission[] => {
  return minifiedPermissions.split(MINIFICATION_DELIMITER).map((m: string) => permissionFromMini[m]).filter(p => p); // throw out things that don't match
}

export enum GlobalUserRole {
  medarriveDeveloper = "medarriveDeveloper",
  selfScheduleProvider = "selfScheduleProvider",
  sampleDataGenerator = "sampleDataGenerator",
}

// We don't give manageOwnAvailability to developers, so that it's easier to test and manage
// this permission via the shift management interface in the command center, and they can - via the UI -
// "manage their own availability" as a provider or pretend to be a manager doing it on a provider's behalf.
const dontGiveToDevs: Permission[] = [Permission.manageOwnAvailability];

// A list of permissions that cannot be added or removed via the UI; those listed below can
// only be modified by editing the database.
export const permissionsNotAssignableInUI = [
  Permission.switchOrganization,
  Permission.debugApps,
  Permission.manageOwnAvailability,
  Permission.isAuthenticated,
  Permission.copyDemoData,
];

export const permissions: Record<GlobalUserRole, Permission[]> = {
  // Developer always has all permissions
  [GlobalUserRole.medarriveDeveloper]: Object.values(Permission).filter(p => !dontGiveToDevs.includes(p)),
  [GlobalUserRole.selfScheduleProvider]: [ Permission.manageOwnAvailability, ],
  // Needs to match what's in apps/service/graphql/externalGqlOperations.ts for the sample data generator
  [GlobalUserRole.sampleDataGenerator]: [
    Permission.managePatients,
    Permission.useWebApp,
    Permission.scheduleVisits,
    Permission.manageAnyVisit,
    Permission.useMobile,
    Permission.impersonateAnyProvider, // Necessary to advance visit status for random providers
  ],
}

export const globalUserRoles: readonly string[] = Object.keys(permissions);
